import { setter }                                from 'mobx-decorators'
import axios                                     from 'axios'
import xhr                                       from 'helpers/XHR'
import codexMegas                                from 'helpers/CodexMegas'

import Broadcast, { SCHEDULED_STATUS }           from 'stores/models/Broadcast'
import getError                                  from 'helpers/ErrorParser'
import dayjs                                     from 'dayjs'
import dayjsUTC                                  from 'dayjs/plugin/utc'
import dayjsTimezone                             from 'dayjs/plugin/timezone'
import uiStore                                   from 'stores/UiStore'
import districtStore                             from 'stores/DistrictStore'
import Pagination                                from 'stores/models/Pagination'
import { postEvent }                             from 'helpers/Segment'
import pluralize                                 from 'helpers/pluralize'
import uuid                                      from 'uuid'

import _                                         from 'lodash'
import { orderBy }                               from 'lodash/fp'

import { observable, action, computed, autorun } from 'mobx'
import StringSize                                from 'helpers/StringSize'

// dayjs.tz needs both plugins; zone data comes from the browser's Intl.
dayjs.extend(dayjsUTC)
dayjs.extend(dayjsTimezone)

const GENERIC_CONTENT_TYPE = 'application/octet-stream'

function detectAssetType(file) {
  const t = file.type || ''
  if (t.startsWith('image/')) return 'image'
  if (t.startsWith('video/')) return 'video'
  if (t.startsWith('audio/')) return 'audio'
  if (t === 'application/pdf') return 'pdf'

  return attachmentTypeFromName(file.name || '')
}

// Stored S3 objects carry no content type, so a refetched blob's own type is useless.
const FILE_TYPE_BY_EXTENSION = {
  png:  { asset: 'image', mime: 'image/png' },
  jpg:  { asset: 'image', mime: 'image/jpeg' },
  jpeg: { asset: 'image', mime: 'image/jpeg' },
  gif:  { asset: 'image', mime: 'image/gif' },
  webp: { asset: 'image', mime: 'image/webp' },
  mp4:  { asset: 'video', mime: 'video/mp4' },
  mov:  { asset: 'video', mime: 'video/quicktime' },
  mp3:  { asset: 'audio', mime: 'audio/mpeg' },
  pdf:  { asset: 'pdf',   mime: 'application/pdf' }
}

// store_attachments prefixes "<YYYYMMDD>_<HHMMSS>_<index>_" -- three segments, not two.
const STORED_NAME_PREFIX = /^\d{8}_\d{6}_\d+_/

function attachmentNameFromUrl(url) {
  const path = String(url).split('?')[0]
  const name = decodeURIComponent(path.split('/').pop() || '')

  return name.replace(STORED_NAME_PREFIX, '')
}

function fileTypeFromName(name) {
  const extension = (String(name).split('.').pop() || '').toLowerCase()

  return FILE_TYPE_BY_EXTENSION[extension]
}

function attachmentTypeFromName(name) {
  const known = fileTypeFromName(name)

  return known ? known.asset : 'file'
}

// The composer stores the icon name it renders, so a reloaded attachment must match.
const COMPOSER_TYPE_BY_ASSET = {
  image: 'image',
  video: 'video',
  pdf:   'pdfs'
}

function composerTypeFromName(name) {
  return COMPOSER_TYPE_BY_ASSET[attachmentTypeFromName(name)] || 'document'
}

function mimeTypeFromName(name) {
  const known = fileTypeFromName(name)

  return known ? known.mime : ''
}

function uploadContentType(file) {
  return file.type || mimeTypeFromName(file.name) || GENERIC_CONTENT_TYPE
}

// The manifest beside the page is the only record of link attachments; turbine has none.
const CODEX_PAGE_FILE = /index\.html$/

function codexManifestUrl(pageUrl) {
  if (!pageUrl || !CODEX_PAGE_FILE.test(pageUrl)) return null

  return pageUrl.replace(CODEX_PAGE_FILE, 'manifest.json')
}

// GSM-7 basic character set + extension table
const GSM7_CHARS = new Set(
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ ÆæßÉ' +
  ' !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZ' +
  'ÄÖÑܧ¿abcdefghijklmnopqrstuvwxyz' +
  'äöñüà^{}\\[~]|€'
)

function isGsm7(text) {
  for (const ch of text) {
    if (!GSM7_CHARS.has(ch)) return false
  }
  return true
}

function truncateSmsBody(text, maxLen) {
  if (text.length <= maxLen) return text
  return text.slice(0, maxLen - 3) + '...'
}

export const ROUTES = {
  TYPE_SELECTION:    'TYPE_SELECTION',
  GROUP_SELECTION:   'GROUP_SELECTION',
  COURSE_SELECTION:  'COURSE_SELECTION',
  STUDENT_SELECTION: 'STUDENT_SELECTION',
  CONTACT_SELECTION: 'CONTACT_SELECTION',
  RECIPIENT_MANAGER: 'RECIPIENT_MANAGER',
  MESSAGE_COMPOSER:  'MESSAGE_COMPOSER',
  PREVIEW_AND_SEND:  'PREVIEW_AND_SEND'
}

export const TYPES = {
  EMAIL: 'email',
  PHONE: 'phone'
}

export const TIMING = {
  NOW:   'now',
  LATER: 'later'
}

// SCHEDULED reads its own turbine endpoint, so paging and totals stay correct.
export const BROADCAST_TAB = {
  ALL:       'all',
  SCHEDULED: 'scheduled'
}

const BROADCAST_ENDPOINTS = {
  [BROADCAST_TAB.ALL]:       'commo/broadcast',
  [BROADCAST_TAB.SCHEDULED]: 'commo/broadcast/scheduled'
}

const STRING_SIZE_ERROR = {
  title:   'Validation Failed',
  message: 'Message body too long, must be less than 128 Kb (copy/pasting from other sources could be adding hidden characters)'
}

const EMAIL_BODY_SIZE_LIMIT = 128000 // 128Kb

const BROADCAST_EMAIL_SUBJECT = 'SchoolStatus Broadcast'

// Shape turbine parses as params[:attachments] => [{ file: ... }]
const BROADCAST_ATTACHMENTS_FIELD = 'attachments[][file]'
export const BROADCAST_BODY_MAX_LENGTH = 2000

// Total across every file, whichever channel. Mirrors turbine's Broadcast::ATTACHMENT_SIZE_LIMIT.
export const BROADCAST_ATTACHMENTS_MAX_MB = 15
export const BROADCAST_ATTACHMENTS_MAX_BYTES = BROADCAST_ATTACHMENTS_MAX_MB * 1024 * 1024

const ATTACHMENT_ERROR_MESSAGES = {
  fileTooLarge: (name) => `${name} is larger than ${BROADCAST_ATTACHMENTS_MAX_MB} MB and can't be attached.`,
  overTotal:    (name) => `Adding ${name} would put your attachments over the ${BROADCAST_ATTACHMENTS_MAX_MB} MB total limit. Remove an attachment or choose a smaller file.`
}

export const CONSTANTS = {
  MAX_CONTACTS: 300000000
}

const BROADCAST_LIMIT = 20

// Turbine lists a sent scheduled broadcast at its send time
const BROADCAST_LIST_SORT = 'sent_at'

const BROADCAST_LIST_FIELDS = [
  'id',
  'body',
  'status',
  'run_at',
  'created_at',
  'user_id',
  'contacts_count',
  'students_count',
  'recipients',
  'user.full_name'
].join(',')

const EDIT_BROADCAST_FIELDS = [
  'id',
  'body',
  'status',
  'run_at',
  'subject',
  'send_to_only_students',
  'send_sms',
  'send_email',
  'auto_translate',
  'codex_page_url',
  'attachments',
  'recipients'
].join(',')

export class BroadcastStore {
  @setter @observable isLoading = false
  @setter @observable isLoadingMore = false
  @setter @observable isLoadingAvailableContacts = false
  @setter @observable isTranslating = false
  @setter @observable isSending = false
  @setter @observable isError = null
  @setter @observable isAutoTranslate = true
  @setter @observable isOnlyStudents = false
  @setter @observable searchValueBroadcast = ''
  @setter @observable isSearchingBroadcasts = false
  @setter @observable currentScroller = `broadcast-search-${uuid()}`
  @setter @observable sendAsEmail = false
  @setter @observable sendAsMessage = false

  // Scheduling. 'YYYY-MM-DD' and 'HH:mm', as the pickers give them.
  @setter @observable sendTiming = TIMING.NOW
  @setter @observable scheduledDate = ''
  @setter @observable scheduledTime = ''

  // Broadcast Attachments
  @setter @observable renderBroadcastLinkInput = false
  @setter @observable broadcastAttachmentLinkValue = ''
  @setter @observable broadcastAttachments = [];

  // Codex Megas page upload
  @setter @observable isUploadingPage = false
  @setter @observable uploadProgress = 0

  // Only display the most recently fetched search results
  @setter @observable fetchRequestID = ''

  // Groups
  @observable groups = observable.map()
  @observable selectedGroups = observable.map()
  @setter @observable isFetchingGroups = false

  //Courses
  @observable courses = observable.map()
  @observable selectedCourses = observable.map()
  @observable terms = observable.map()
  @observable teachers = observable.map()
  @observable selectedTeacher = null
  @observable selectedTerm = null
  @setter @observable isFetchingTeachers = false
  @setter @observable isFetchingTerms = false
  @setter @observable isFetchingCourses = false
  @setter @observable searchValueTeachers = ''
  @setter @observable isTypingTeacherSearch = false

  // Unobserved on purpose: an autorun drives these fetches.
  termsFetchID   = null
  coursesFetchID = null

  //Contacts
  @observable contacts = observable.map()
  @observable selectedContacts = observable.map()
  @observable selectedStudents = observable.map()
  @observable students = observable.map()
  @observable searchedStudents = observable.map()
  @setter @observable isFetchingStudents = false
  @setter @observable isSearchingStudents = false
  @setter @observable isFetchingContacts = false
  @setter @observable invalidContacts = observable.map()
  @setter @observable validContacts = observable.map()
  @setter @observable emailContacts = observable.map()
  @setter @observable phoneContacts = observable.map()

  //Email
  @observable conversationIDs = []
  @setter @observable isFetchingConversations = false

  //Meta
  @setter @observable LanguageInfo = {
    languages: []
  }

  //Navigation
  @setter @observable currentRoute = ROUTES.TYPE_SELECTION
  routesVisited = [ROUTES.TYPE_SELECTION]

  @setter @observable selectedBroadcast = null
  @setter @observable shouldShowModal = false
  @setter @observable count = 0
  @setter @observable phoneCount = 0
  @setter @observable emailCount = 0
  @setter @observable fetchingCounts = false
  @setter @observable currentStudent = null
  @setter @observable body = ''
  @setter @observable translatedBody = null

  @observable broadcasts = observable.map()
  @observable pagination = new Pagination(this)
  @setter @observable totalBroadcasts = 0
  @setter @observable broadcastTab = BROADCAST_TAB.ALL
  @setter @observable showDeleteScheduledModal = false
  @setter @observable isDeletingScheduled = false

  @setter @observable editingBroadcastId = null
  @setter @observable showDiscardModal = false
  @setter @observable showSendNowModal = false
  @setter @observable isPreparingEdit = false

  @setter @observable editCodexPageUrl = null
  @setter @observable editSubject = null

  constructor() {
    this.initAutoruns()

    this.debouncedHandleSearch = _.debounce(
      this.handleSearchTeachers,
      250
    )

    this.debouncedHandleBroadcastSearch = _.debounce(
      this.handleSearchBroadcasts,
      750
    )
  }

  @computed
  get studentParams() {
    return {
      page:      this.pagination.current,
      limit:     this.pagination.pageSize,
      direction: 'asc',
      sort:      'last_name',
      only:      [
        'avatar_url',
        'full_name',
        'first_name',
        'last_name',
        'id',
        'grade',
        'school.school_name'
      ].join(',')
    }
  }

  @computed
  get hasTranslatableBody() {
    // Auto-translate is meaningless when the message body is empty or
    // whitespace-only (e.g. a photo-only broadcast). We use this to avoid
    // asking codex-megas to generate per-language pages that would never
    // differ from the default English render, which in turn prevents
    // turbine from rewriting recipient URLs to index.{lang}.html variants
    // that don't exist on S3.
    return typeof this.body === 'string' && this.body.trim() !== ''
  }

  @computed
  get sendParams() {
    return {
      contacts:              [...this.selectedContacts.keys()].join(','),
      groups:                [...this.selectedGroups.keys()].join(','),
      courses:               [...this.selectedCourses.keys()].join(','),
      students:              [...this.selectedStudents.keys()].join(','),
      body:                  this.body,
      auto_translate:        this.isAutoTranslate && this.hasTranslatableBody,
      send_to_only_students: this.isOnlyStudents,
      params:                {
        only: [
          'id',
          'body',
          'created_at',
          'user_id',
          'contacts_count',
          'students_count',
          'recipients',
          'contacts.name',
          'contacts.id',
          'contacts.student_id',
          'contacts.student.full_name',
          'contacts.relationship'
        ].join(',')
      }
    }
  }

  @computed
  get recipientsList() {
    return (
      this.selectedGroups.keys().map((g) => {
        return { type: 'group', id: g }
      }) || []
    ).concat(
      this.selectedCourses.keys().map((c) => {
        return { type: 'course', id: c }
      }) || [],
      this.selectedContacts.keys().map((c) => {
        return { type: 'individual', id: c }
      }) || [],
      this.selectedStudents.keys().map((s) => {
        return { type: 'student', id: s }
      }) || []
    )
  }

  // Turbine keeps canceled rows for audit and still lists them, so drop them here.
  @computed
  get listedBroadcasts() {
    return this.broadcasts.values().filter((b) => !b.isCanceled)
  }

  // Same order as turbine's sort=sent_at, id breaking ties.
  @computed
  get descBroadcasts() {
    return orderBy(
      [(b) => b.listedAtTime, 'id'],
      ['desc', 'desc']
    )(this.listedBroadcasts)
  }

  // Soonest-first, matching turbine's `order(:run_at)` so paging stays in order.
  @computed
  get scheduledBroadcasts() {
    return orderBy(
      (b) => b.runAt,
      'asc'
    )(this.listedBroadcasts)
  }

  @computed
  get visibleBroadcasts() {
    return this.isScheduledTab ? this.scheduledBroadcasts : this.descBroadcasts
  }

  // Drives the endpoint, sort, search scope and copy together.
  @computed
  get isScheduledTab() {
    return this.broadcastTab === BROADCAST_TAB.SCHEDULED
  }

  @computed
  get shouldShowFooter() {
    return this.currentRoute !== ROUTES.TYPE_SELECTION
  }

  @computed
  get isComposing() {
    return this.currentRoute === ROUTES.MESSAGE_COMPOSER
  }

  @computed
  get isReviewing() {
    return this.currentRoute === ROUTES.PREVIEW_AND_SEND
  }

  @computed
  get isLoadingMoreStudents() {
    return (
      this.isFetchingStudents &&
      !this.searchedStudents.size &&
      this.students.size
    )
  }

  @computed
  get hasRecipients() {
    return (
      this.selectedCourses.size ||
      this.selectedGroups.size ||
      this.selectedContacts.size ||
      this.selectedStudents.size
    )
  }

  @computed
  get hasContacts() {
    return this.count > 0
  }

  @computed
  get hasValidBody() {
    return !_.isEmpty(this.body) && this.body.length <= BROADCAST_BODY_MAX_LENGTH
  }

  @computed
  get isBodyTooLong() {
    return this.body.length > BROADCAST_BODY_MAX_LENGTH
  }

  @computed
  get hasValidSendType(){
    return this.sendAsEmail || this.sendAsMessage
  }

  // Zeroed until recipients resolve, so the footer and microcopy always agree.
  @computed
  get deliveryCounts() {
    const isResolved = this.hasRecipients && this.hasContacts

    return {
      phone: isResolved ? this.phoneCount : 0,
      email: isResolved ? this.emailCount : 0
    }
  }

  // Email-only for now. Links have no file to upload -- they live on the codex page.
  @computed
  get emailAttachments() {
    if (!this.sendAsEmail) return []

    return this.broadcastAttachments.filter((a) => a.type !== 'link')
  }

  // Links have no size; only files count toward BROADCAST_ATTACHMENTS_MAX_BYTES.
  @computed
  get attachmentsTotalBytes() {
    return _.sumBy(
      this.broadcastAttachments.filter((a) => a.type !== 'link'),
      (a) => a.value.size || 0
    )
  }

  @computed
  get attachmentsTotalMB() {
    return (this.attachmentsTotalBytes / (1024 * 1024)).toFixed(2)
  }

  // Turbine appends these to the email body; SMS reaches them via the codex page.
  @computed
  get broadcastLinks() {
    return this.broadcastAttachments
      .filter((a) => a.type === 'link')
      .map((a) => a.value)
  }

  // e.g. "Will be sent to 13 phones and 32 emails".
  @computed
  get deliverySummary() {
    const { phone, email } = this.deliveryCounts
    const parts = []

    if (this.sendAsMessage) parts.push(`${phone} ${pluralize(phone, 'phone')}`)
    if (this.sendAsEmail) parts.push(`${email} ${pluralize(email, 'email')}`)

    if (_.isEmpty(parts)) return ''

    return `Will be sent to ${parts.join(' and ')}`
  }

  // Every scheduling behavior reads this.
  @computed
  get isScheduled() {
    return this.sendTiming === TIMING.LATER
  }

  // IANA zone, fetched once per session.
  @computed
  get districtTimeZoneName() {
    return districtStore.timeZoneName
  }

  // 2:30 PM means 2:30 PM in the district, wherever the sender is.
  @computed
  get runAt() {
    if (!this.isScheduled) return null
    if (!this.districtTimeZoneName) return null
    if (!this.scheduledDate || !this.scheduledTime) return null

    return dayjs
      .tz(`${this.scheduledDate} ${this.scheduledTime}`, this.districtTimeZoneName)
      .toDate()
  }

  // turbine 403s a past run_at; catch it here first.
  @computed
  get isScheduledInPast() {
    return !!this.runAt && this.runAt <= new Date()
  }

  @computed
  get hasValidSchedule() {
    if (!this.isScheduled) return true

    return !!this.runAt && !this.isScheduledInPast
  }

  // Label only. tz to show, tz_name to convert -- the split notify uses.
  @computed
  get districtTimeZone() {
    const tz = window.SSUser && window.SSUser.districtTz

    return (tz || '').toUpperCase()
  }

  // Today where the district is, not where the sender is.
  @computed
  get scheduleMinDate() {
    if (!this.districtTimeZoneName) return dayjs().format('YYYY-MM-DD')

    return dayjs().tz(this.districtTimeZoneName).format('YYYY-MM-DD')
  }

  @computed
  get canSend() {
    return (
      this.hasValidBody &&
      this.hasContacts &&
      this.hasValidSendType &&
      this.hasValidSchedule &&
      !this.isFetchingConversations
    )
  }

  @computed
  get isEditingScheduled() {
    return !!this.editingBroadcastId
  }

  @computed
  get isSendingScheduledNow() {
    return this.isEditingScheduled && this.sendTiming === TIMING.NOW
  }

  @computed
  get editRunAt() {
    const fallback = dayjs().add(1, 'hour')

    if (!this.districtTimeZoneName || !this.scheduledDate || !this.scheduledTime) {
      return fallback.toDate()
    }

    const runAt = dayjs.tz(
      `${this.scheduledDate} ${this.scheduledTime}`,
      this.districtTimeZoneName
    )

    return (runAt.isValid() && runAt.isAfter(dayjs()) ? runAt : fallback).toDate()
  }

  @computed
  get updateScheduledParams() {
    return {
      body:                  this.body,
      subject:               this.editSubject || BROADCAST_EMAIL_SUBJECT,
      contacts:              [...this.selectedContacts.keys()].join(','),
      groups:                [...this.selectedGroups.keys()].join(','),
      courses:               [...this.selectedCourses.keys()].join(','),
      students:              [...this.selectedStudents.keys()].join(','),
      auto_translate:        this.isAutoTranslate && this.hasTranslatableBody,
      send_to_only_students: this.isOnlyStudents,
      send_sms:              !!this.sendAsMessage,
      send_email:            !!this.sendAsEmail,
      codex_page_url:        this.editCodexPageUrl,
      links:                 this.broadcastLinks,
      run_at:                this.editRunAt.toISOString()
    }
  }

  // Every file, not just the email ones: the PUT deletes anything left out.
  @computed
  get updateScheduledAttachments() {
    return this.broadcastAttachments.filter((a) => a.type !== 'link')
  }

  @computed
  get overMaxContacts() {
    return false
  }

  @computed
  get shouldSearch() {
    if (!_.isEmpty(this.searchValueTeachers)) {
      return (
        this.searchValueTeachers.length >= 2 &&
        !this.isFetchingTeachers
      )
    } else return false
  }

  @computed
  get shouldSearchBroadcast() {
    if (!_.isEmpty(this.searchValueBroadcast)) {
      return (
        this.searchValueBroadcast.length >= 3 &&
        (!this.isLoading || !this.isLoadingMore)
      )
    } else return false
  }

  @computed
  get isEmptyList(){
    return this.broadcasts.size == 0 && !this.isLoading && !this.isLoadingMore && !this.isSearchingBroadcasts
  }

  @computed
  get broadcastListEndpoint() {
    return BROADCAST_ENDPOINTS[
      this.isScheduledTab ? BROADCAST_TAB.SCHEDULED : BROADCAST_TAB.ALL
    ]
  }

  // undefined is dropped from the query string, so the All tab is unchanged.
  @computed
  get broadcastSearchStatus() {
    return this.isScheduledTab ? SCHEDULED_STATUS : undefined
  }

  @computed
  get shouldFetchMore(){
    return (this.broadcasts.size < this.totalBroadcasts) && !this.isLoading && !this.isLoadingMore && !this.isSearchingBroadcasts
  }

  @action
  initAutoruns = () => {
    this.autoErrorNotifier()
    this.autoTermFetcher()
    this.autoCourseFetcher()
  }

  @action
  autoErrorNotifier = () => {
    this.autoErrorDisposer = autorun(() => {
      if (this.isError && !this.isError.hideNotification) {
        uiStore.addNotification({
          title:   this.isError.title,
          message: this.isError.message,
          type:    this.isError.type || 'error'
        })
      }
    })
  }

  // Clearing the teacher must clear everything the teacher scoped.
  @action resetTeacherSelection = () => {
    this.selectedTeacher = null
    this.selectedTerm = null
    this.terms.clear()
    this.courses.clear()
  }

  @action autoTermFetcher = () => {
    this.autoTermFetcher = autorun(() => {
      if (this.selectedTeacher) {
        this.fetchTerms()
      }
    })
  }

  @action autoCourseFetcher = () => {
    this.autoCourseFetcher = autorun(() => {
      if (this.selectedTerm) {
        this.fetchCourses()
      }
    })
  }

  @action clearData = () => {
    this.broadcasts.clear()
  }

  @action setPagination = ({ total }) => {
    this.pagination.setTotal(parseInt(total))
    this.pagination.calculateTotal
  }

  @action
  fetchBroadcasts = async() => {
    try {
      this.setCurrentScroller(`broadcast-search-${uuid()}`)
      this.setIsLoadingMore(false)
      this.setIsLoading(true)
      this.setIsError(false)
      this.setFetchRequestID(uuid())
      const currentFetchID = this.fetchRequestID

      // Scheduled rows print run_at in the district's zone. Cached after the first.
      if (this.isScheduledTab) this.fetchDistrictTimeZone()

      const { headers, data } = await xhr.get(this.broadcastListEndpoint, {
        params: {
          limit:     BROADCAST_LIMIT,
          page:      1,
          direction: 'desc',
          sort:      BROADCAST_LIST_SORT,
          only:      BROADCAST_LIST_FIELDS
        }
      })

     if(currentFetchID === this.fetchRequestID){
      this.setTotalBroadcasts(+headers.total)
      this.fetchBroadcastsOk(data)
     }
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsLoading(false)
    }
  }

  @action
  fetchBroadcastsOk = (broadcasts) => {
    broadcasts.forEach(this.createBroadcast)
  }

  @action
  fetchOrSearchMoreBroadcasts = async(page) => {
    this.shouldSearchBroadcast
    ? this.searchMoreBroadcasts(page)
    : this.fetchMoreBroadcasts(page)

  }

  @action
  fetchMoreBroadcasts = async(page) => {
    try {
      this.setIsLoading(false)
      this.setIsLoadingMore(true)
      this.setIsError(false)
      this.setFetchRequestID(uuid())
      const currentFetchID = this.fetchRequestID

      const { data } = await xhr.get(this.broadcastListEndpoint, {
        params: {
          limit:     BROADCAST_LIMIT,
          page:      page,
          direction: 'desc',
          sort:      BROADCAST_LIST_SORT,
          only:      BROADCAST_LIST_FIELDS
        }
      })

      if(currentFetchID === this.fetchRequestID){
        this.fetchMoreBroadcastsOk(data)
      }
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsLoadingMore(false)
    }
  }

  @action fetchMoreBroadcastsOk = (broadcasts) => {
    broadcasts.forEach(this.createBroadcast)
  }

  // A refetch carries the latest status and run_at, so refresh a row that's already
  // loaded -- otherwise a broadcast that sent stays "Scheduled" until a reload.
  @action
  createBroadcast = (broadcast) => {
    const existing = this.broadcasts.get(broadcast.id)
    if (existing) return existing.update(broadcast)

    this.broadcasts.set(broadcast.id, new Broadcast(this, broadcast))
  }

  // The list payload no longer includes the full contact roster (it caused
  // heavy per-row roster expansion + N+1 on every list request). Fetch it
  // lazily for the single broadcast being viewed.
  @action
  fetchBroadcastContacts = async(broadcast) => {
    // Already loaded, or a fetch is already in flight -- nothing to do.
    if (broadcast.contacts != null || broadcast.isLoadingContacts) return

    broadcast.setIsLoadingContacts(true)

    try {
      const { data } = await xhr.get(`commo/broadcast/${broadcast.id}`, {
        params: {
          only: [
            'id',
            'contacts.name',
            'contacts.id',
            'contacts.student_id',
            'contacts.student.full_name',
            'contacts.relationship'
          ].join(',')
        }
      })

      broadcast.setContacts(_.get(data, 'contacts', []))
    } catch (err) {
      // Leave `contacts` null so reopening retries; surface via the model's toast.
      broadcast.setIsError(getError(err))
    } finally {
      broadcast.setIsLoadingContacts(false)
    }
  }

  /* Search */
  @action
  searchBroadcasts = async (searchText) => {
    try {
      this.setCurrentScroller(`broadcast-search-${uuid()}`)
      this.clearData()
      this.setIsLoadingMore(false)
      this.setIsLoading(true)
      this.setIsError(false)
      this.setIsSearchingBroadcasts(true)
      this.setFetchRequestID(uuid())
      const currentFetchID = this.fetchRequestID

      const { headers, data } = await xhr.get('commo/broadcast/search', {
        params: {
          page:      1,
          query:     searchText,
          limit:     BROADCAST_LIMIT,
          status:    this.broadcastSearchStatus,
          sort:      BROADCAST_LIST_SORT,
          only:      BROADCAST_LIST_FIELDS
        }
      })

      if(currentFetchID === this.fetchRequestID){
        const amount = parseInt(headers['content-length'])

        this.setTotalBroadcasts(amount)
        this.searchBroadcastsOk(data)
      }
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsLoading(false)
      this.setIsSearchingBroadcasts(false)
    }
  }

  @action
  searchBroadcastsOk = (broadcasts) => {
    broadcasts.forEach(this.createBroadcast)
  }

  @action
  searchMoreBroadcasts = async(page) => {
    try {
      this.setIsLoading(false)
      this.setIsLoadingMore(true)
      this.setIsError(false)
      this.setFetchRequestID(uuid())
      const currentFetchID = this.fetchRequestID

      const { data } = await xhr.get('commo/broadcast/search', {
        params: {
          page:   page,
          query:  this.searchValueBroadcast,
          limit:  BROADCAST_LIMIT,
          status: this.broadcastSearchStatus,
          sort:   BROADCAST_LIST_SORT,
          only:   BROADCAST_LIST_FIELDS
        }
      })

      if(currentFetchID === this.fetchRequestID){
        this.searchMoreBroadcastsOk(data)
      }
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsLoadingMore(false)
    }
  }

  @action
  searchMoreBroadcastsOk = (broadcasts) => {
    broadcasts.forEach(this.createBroadcast)
  }
  /* Fetches */

  @action
  fetchStudentGroups = async() => {
    try {
      this.setIsFetchingGroups(true)
      const { data } = await xhr.get('groups', {
        params: {
          type: 'student',
          only: ['id', 'group_name', 'member_count'].join(',')
        }
      })

      data.forEach((g) => {
        this.groups.set(g.id, g)
      })
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsFetchingGroups(false)
    }
  }

  @action
  fetchTeachers = async() => {
    try {
      this.setIsFetchingTeachers(true)
      this.courses.clear()
      const { data } = await xhr.get('teachers', {
        params: {
          limit:        100,
          only_current: false,
          only:         ['id', 'first_name', 'last_name'].join(',')
        }
      })

      data.forEach((t) => {
        this.teachers.set(t.id, t)
      })
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsFetchingTeachers(false)
    }
  }

  @action
  searchTeachers = async(searchText) => {
    try {
      this.setIsFetchingTeachers(true)
      this.courses.clear()
      const { data } = await xhr.get('teachers/search', {
        params: {
          limit:        100,
          text:         searchText,
          only:         ['id', 'first_name', 'last_name'].join(','),
          only_current: true
        }
      })

      data.forEach((t) => {
        this.teachers.set(t.id, t)
      })
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsFetchingTeachers(false)
      this.setIsTypingTeacherSearch(false)
    }
  }

  @action
  fetchTerms = async() => {
    if (!this.selectedTeacher) return this.terms.clear()

    const fetchID = (this.termsFetchID = uuid())

    try {
      this.terms.clear()
      this.setIsFetchingTerms(true)
      const { data } = await xhr.get('courses/teacher_terms', {
        params: {
          teacher_id: this.selectedTeacher
        }
      })

      if (fetchID !== this.termsFetchID) return

      this.terms.clear()
      data.forEach((t) => {
        this.terms.set(t, t)
      })
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      if (fetchID === this.termsFetchID) this.setIsFetchingTerms(false)
    }
  }

  // Never fetch unscoped: an absent teacher_id returns the whole school.
  @action
  fetchCourses = async() => {
    if (!this.selectedTeacher || !this.selectedTerm) return this.courses.clear()

    const fetchID = (this.coursesFetchID = uuid())

    try {
      this.courses.clear()
      this.setIsFetchingCourses(true)
      const { data } = await xhr.get('courses/teacher_courses', {
        params: {
          teacher_id: this.selectedTeacher,
          term:       this.selectedTerm
        }
      })

      if (fetchID !== this.coursesFetchID) return

      this.courses.clear()
      data.forEach((t) => {
        this.courses.set(t.id, t)
      })
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      if (fetchID === this.coursesFetchID) this.setIsFetchingCourses(false)
    }
  }

  @action
  fetchStudents = async() => {
    try {
      this.pagination.clear()
      this.setIsFetchingStudents(true)
      this.searchedStudents.clear()
      this.students.clear()
      const res = await xhr.get('students', {
        params: this.studentParams
      })

      this.fetchStudentsOk(res)
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsFetchingStudents(false)
    }
  }

  @action
  fetchStudentsOk = ({ data, headers }) => {
    this.setPagination(headers)

    data.forEach((s) => {
      this.students.set(s.id, s)
    })
  }

  @action
  fetchMoreStudents = async() => {
    try {
      this.searchedStudents.clear()
      this.setIsFetchingStudents(true)
      const res = await xhr.get('students', {
        params: this.studentParams
      })

      this.fetchMoreStudentsOk(res)
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsFetchingStudents(false)
    }
  }

  @action
  fetchMoreStudentsOk = ({ data }) => {
    this.pagination.setCurrent(this.pagination.current + 1)

    data.forEach((s) => {
      this.students.set(s.id, s)
    })
  }

  @action
  searchStudents = async(text) => {
    try {
      this.setIsSearchingStudents(true)
      const { data } = await xhr.get('typeahead/students', {
        params: {
          text_filter: text
        }
      })

      this.searchStudentsOk(data)
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsSearchingStudents(false)
    }
  }

  @action
  searchStudentsOk = (students) => {
    this.searchedStudents.clear()
    students.forEach((s) => {
      this.searchedStudents.set(s.id, s)
    })
  }

  @action
  fetchContacts = async() => {
    if (!this.currentStudent) return

    try {
      this.contacts.clear()
      this.setIsFetchingContacts(true)
      const { data } = await xhr.get(
        `students/${this.currentStudent.id}/contacts_for_broadcast`,
        {
          params: {
            only: [
              'id',
              'name',
              'primary',
              'relationship',
              'student.full_name',
              'student.id',
              'phone',
              'phone_type'
            ].join(','),
            include_contact_phone_type: 'true'
          }
        }
      )

      data.forEach((c) => {
        this.contacts.set(c.id, c)
      })
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsFetchingContacts(false)
    }
  }

  @action
  fetchCount = async() => {
    const groups = this.selectedGroups.keys().join(',')
    const contacts = this.selectedContacts.keys().join(',')
    const courses = this.selectedCourses.keys().join(',')
    const students = this.selectedStudents.keys().join(',')
    this.setFetchingCounts(true)
    try {
      const { data } = await xhr.post('commo/broadcast/web_count', {
        groups,
        contacts,
        courses,
        students,
        send_to_only_students: this.isOnlyStudents
      })
      this.setFetchingCounts(false)
      this.setEmailCount(data.email)
      this.setPhoneCount(data.phone)
      this.setCount(data.total)
      this.fetchLanguages()
    } catch (err) {
      this.setIsError(getError(err))
    }

  }

  @action
  fetchAvailableContacts = async(type = TYPES.PHONE) => {
    const groups = this.selectedGroups.keys().join(',')
    const contacts = this.selectedContacts.keys().join(',')
    const courses = this.selectedCourses.keys().join(',')
    const students = this.selectedStudents.keys().join(',')

    try {
      this.setIsLoadingAvailableContacts(true)
      const { data } = await xhr.get('commo/broadcast/get_contacts', {
        params: {
          type,
          groups,
          contacts,
          courses,
          students,
          send_to_only_students: this.isOnlyStudents
        }
      })

      if (type === TYPES.EMAIL) this.fetchInvalidContactsOk(data)
      if (type === TYPES.PHONE) this.fetchValidContactsOk(data)
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsLoadingAvailableContacts(false)
    }
  }

  @action
  fetchInvalidContactsOk = (contacts) => {
    this.invalidContacts.clear()
    contacts.forEach((c) => {
      this.invalidContacts.set(c.id, c)
    })
  }

  @action
  fetchValidContactsOk = (contacts) => {
    this.validContacts.clear()
    contacts.forEach((c) => {
      this.validContacts.set(c.id, c)
    })
  }

  @action
  fetchLanguages = async() => {
    const groups = this.selectedGroups.keys().join(',')
    const contacts = this.selectedContacts.keys().join(',')
    const courses = this.selectedCourses.keys().join(',')
    const students = this.selectedStudents.keys().join(',')

    try {
      const { data } = await xhr.post('commo/broadcast/languages', {
        groups,
        contacts,
        courses,
        students,
        send_to_only_students: this.isOnlyStudents
      })

      this.setLanguageInfo(data)
    } catch (err) {
      this.setIsError(getError(err))
    }
  }

  // Cached in DistrictStore, so repeat trips through the wizard are free.
  @action
  fetchDistrictTimeZone = () => {
    return districtStore.getDistrictTimeZoneName(window.SSUser.districtID)
  }

  @action
  fetchConversations = () => {
    this.setIsFetchingConversations(true)

    try {
      this.recipientsList.map(({ type, id }) => {
        xhr
          .get('/commo/email/get_conversation', {
            params: {
              id,
              type,
              only: ['id'].join(',')
            }
          })
          .then((res) => {
            if (!this.conversationIDs.includes(res.data.id))
              this.conversationIDs.push(res.data.id)
          })
      })
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.setIsFetchingConversations(false)
    }
  }

  @action
  translateBody = async(target) => {
    this.setIsTranslating(true)

    try {
      if (!target) this.setTranslatedBody(this.body)
      if (target && this.body.length) {
        const { data } = await xhr.post('commo/translate', {
          text: this.body,
          target
        })

        this.setTranslatedBody(data.translation)
      }
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsTranslating(false)
    }
  }

  /* Selections */

  @action
  handleGroupSelection = (group) => {
    if (this.selectedGroups.has(group.id)) {
      this.selectedGroups.delete(group.id)
    } else {
      this.selectedGroups.set(group.id, group)
    }

    this.fetchCount()
  }

  @action
  handleCourseSelection = (course) => {
    if (this.selectedCourses.has(course.id)) {
      this.selectedCourses.delete(course.id)
    } else {
      this.selectedCourses.set(course.id, course)
    }

    this.fetchCount()
  }

  @action
  handleContactSelection = (contact) => {
    if (this.selectedContacts.has(contact.id)) {
      this.selectedContacts.delete(contact.id)
    } else {
      this.selectedContacts.set(contact.id, contact)
    }

    this.fetchCount()
  }

  @action
  handleStudentSelection = (student) => {
    if (this.selectedStudents.has(student.id)) {
      this.selectedStudents.delete(student.id)
    } else {
      this.selectedStudents.set(student.id, student)
    }

    this.fetchCount()
  }

  @action
  launchBroadcastModal = () => {
    this.resetRoutes()
    this.resetTeacherSelection()
    this.setCurrentRoute(ROUTES.TYPE_SELECTION)
    this.setShouldShowModal(true)
  }

  // Open the broadcast modal with the audience pre-seeded to a set of students
  // (objects with at least `id`; the backend resolves each to its preferred
  // contacts). Lands on the composer since the audience is already complete.
  @action
  launchBroadcastWithStudents = (students = []) => {
    this.clearRecipients()
    this.resetRoutes()
    this.resetTeacherSelection()
    students.forEach((s) => {
      const student = (typeof s === 'object' && s !== null) ? s : { id: s }
      this.selectedStudents.set(student.id, student)
    })
    this.setCurrentRoute(ROUTES.MESSAGE_COMPOSER)
    this.setShouldShowModal(true)
    this.fetchCount()
  }

  // Open the broadcast modal with the audience pre-seeded to a set of courses
  // (objects with at least `id`; the backend resolves each course to its
  // students' preferred contacts). Lands on the composer since the audience is
  // already complete. Mirrors launchBroadcastWithStudents.
  @action
  launchBroadcastWithCourses = (courses = []) => {
    this.clearRecipients()
    this.resetRoutes()
    this.resetTeacherSelection()
    courses.forEach((c) => {
      const course = (typeof c === 'object' && c !== null) ? c : { id: c }
      this.selectedCourses.set(course.id, course)
    })
    this.setCurrentRoute(ROUTES.MESSAGE_COMPOSER)
    this.setShouldShowModal(true)
    this.fetchCount()
  }

  @action
  resetBroadcast = () => {
    this.setShouldShowModal(false)
    this.setBroadcastAttachments([])
    this.setBroadcastAttachmentLinkValue('');
    this.setRenderBroadcastLinkInput(false);
    this.setSendAsMessage(false)
    this.setSendAsEmail(false)
    this.setSendTiming(TIMING.NOW)
    this.setScheduledDate('')
    this.setScheduledTime('')
    this.clearRecipients()
    this.setBody('')
    this.setBroadcastAttachments([])
    this.setIsUploadingPage(false)
    this.resetTeacherSelection()
    this.teachers.clear()
    this.setSearchValueTeachers('')
    this.setEditingBroadcastId(null)
    this.setShowDiscardModal(false)
    this.setShowSendNowModal(false)
    this.setEditCodexPageUrl(null)
    this.setEditSubject(null)
  }

  @action
  resetRoutes = () => {
    this.routesVisited = [ROUTES.TYPE_SELECTION]
  }

  @action
  clearRecipients = () => {
    this.selectedGroups.clear()
    this.selectedCourses.clear()
    this.selectedContacts.clear()
    this.selectedStudents.clear()
    this.validContacts.clear()
    this.invalidContacts.clear()
    this.conversationIDs.clear()
  }

  @action
  handleSelectTeacher = (teacherId) => {
    this.terms.clear()
    this.courses.clear()
    this.selectedTerm = null
    this.selectedTeacher = teacherId
  }

  @action
  handleSearchTeachers = (value) => {
    this.setSearchValueTeachers(value)
    this.setIsTypingTeacherSearch(true)
    if (!this.shouldSearch) return
    this.searchTeachers(this.searchValueTeachers)
  }

  // The search box is shared, so an active query carries over rescoped.
  @action
  handleBroadcastTabChange = (tab) => {
    if (this.broadcastTab === tab) return

    this.setBroadcastTab(tab)
    this.setTotalBroadcasts(0)

    if (this.isScheduledTab) this.fetchDistrictTimeZone()

    if (this.shouldSearchBroadcast) {
      return this.searchBroadcasts(this.searchValueBroadcast)
    }

    this.clearData()
    this.fetchBroadcasts()
  }

  // Turbine soft-cancels (status -> canceled), so the row leaves the scheduled list.
  @action
  deleteScheduledBroadcast = async() => {
    const broadcast = this.selectedBroadcast
    if (!broadcast || this.isDeletingScheduled) return

    this.setIsDeletingScheduled(true)
    this.setIsError(false)

    try {
      await xhr.delete(`commo/broadcast/${broadcast.id}/scheduled`)
      this.deleteScheduledBroadcastOk()
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsDeletingScheduled(false)
    }
  }

  @action
  deleteScheduledBroadcastOk = () => {
    this.setShowDeleteScheduledModal(false)
    this.setSelectedBroadcast(null)
    uiStore.setShowBroadcastInfo(false)
    this.resetBroadcastList()
  }

  @action
  resetBroadcastList = () => {
    this.setBroadcastTab(BROADCAST_TAB.ALL)
    this.setSearchValueBroadcast('')
    this.setTotalBroadcasts(0)
    this.clearData()
    this.fetchBroadcasts()
  }

  // Same tab and query -- an edited run_at reorders the scheduled list.
  @action
  refreshBroadcastList = () => {
    this.setTotalBroadcasts(0)
    this.clearData()

    if (this.shouldSearchBroadcast) return this.searchBroadcasts(this.searchValueBroadcast)

    this.fetchBroadcasts()
  }

  @action
  launchEditScheduledBroadcast = async(broadcast) => {
    if (!broadcast || !broadcast.isScheduled || this.isPreparingEdit) return

    this.setIsPreparingEdit(true)
    this.setIsError(false)

    try {
      const { data } = await xhr.get(`commo/broadcast/${broadcast.id}`, {
        params: { only: EDIT_BROADCAST_FIELDS }
      })

      if (data.status !== SCHEDULED_STATUS) {
        return this.setIsError({
          title:   'Broadcast already sent',
          message: 'This broadcast is no longer scheduled and can no longer be edited.'
        })
      }

      // run_at is split in the district's zone, so it has to resolve first.
      await this.fetchDistrictTimeZone()

      await this.seedEditFromBroadcast(data)
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsPreparingEdit(false)
    }
  }

  @action
  seedEditFromBroadcast = async(data) => {
    this.resetBroadcast()

    this.setEditingBroadcastId(data.id)
    this.setBody(data.body || '')
    this.setSendTiming(TIMING.LATER)
    this.setIsOnlyStudents(!!data.send_to_only_students)
    this.setEditCodexPageUrl(data.codex_page_url || null)
    this.setEditSubject(data.subject || null)
    this.seedDeliveryFromBroadcast(data)
    this.seedScheduleFromRunAt(data.run_at)
    this.seedRecipientsFromBroadcast(data)

    await this.loadExistingAttachments(data.attachments)
    await this.loadCodexLinks(data.codex_page_url)

    this.routesVisited = [
      ROUTES.TYPE_SELECTION,
      ROUTES.RECIPIENT_MANAGER,
      ROUTES.MESSAGE_COMPOSER,
      ROUTES.PREVIEW_AND_SEND
    ]
    this.setCurrentRoute(ROUTES.PREVIEW_AND_SEND)

    this.setShouldShowModal(true)
    this.fetchCount()
  }

  @action
  loadExistingAttachments = async(urls = []) => {
    const loaded = await Promise.all(urls.map(this.fetchAttachmentFile))

    loaded.filter(Boolean).forEach(({ type, value }) => this.addAttachment(type, value))
  }

  @action
  loadCodexLinks = async(pageUrl) => {
    const url = codexManifestUrl(pageUrl)

    if (!url) return

    try {
      const response = await fetch(url)

      if (!response.ok) throw new Error(`Manifest request failed (${response.status})`)

      const manifest = await response.json()

      ;(manifest.assets || [])
        .filter((asset) => asset.type === 'link' && asset.filename)
        .forEach((asset) => this.addAttachment('link', asset.filename))
    } catch (err) {
      // Hedged: a failure can't tell us whether there were any links.
      this.setIsError({
        title:   'Links may be missing',
        message: "We couldn't load the links attached to this broadcast. If it had any, please add them again before saving.",
        type:    'warning'
      })

      console.warn('Could not restore link attachments from the codex page', err)
    }
  }

  fetchAttachmentFile = async(url) => {
    const name = attachmentNameFromUrl(url)

    try {
      const response = await fetch(url)

      if (!response.ok) throw new Error(`Attachment request failed (${response.status})`)

      const blob = await response.blob()

      // Untyped here means codex renders the attachment as a bare download link.
      const blobType = blob.type && blob.type !== GENERIC_CONTENT_TYPE ? blob.type : ''

      return {
        type:  composerTypeFromName(name),
        value: new File([blob], name, { type: blobType || mimeTypeFromName(name) })
      }
    } catch (err) {
      this.setIsError({
        title:   'Attachment',
        message: `Could not load "${name}". Saving now would remove it from this broadcast.`
      })

      return null
    }
  }

  @action
  seedDeliveryFromBroadcast = (data) => {
    const known = (value) => value !== undefined && value !== null

    if (known(data.send_sms)) this.setSendAsMessage(!!data.send_sms)
    if (known(data.send_email)) this.setSendAsEmail(!!data.send_email)
    if (known(data.auto_translate)) this.setIsAutoTranslate(!!data.auto_translate)
  }

  @action
  seedScheduleFromRunAt = (runAt) => {
    if (!runAt) return

    const zone  = this.districtTimeZoneName
    const local = zone ? dayjs(runAt).tz(zone) : dayjs(runAt)

    this.setScheduledDate(local.format('YYYY-MM-DD'))
    this.setScheduledTime(local.format('HH:mm'))
  }

  @action
  seedRecipientsFromBroadcast = (broadcast) => {
    const recipients = broadcast.recipients || []

    recipients.forEach(({ type, group, course, contact, student }) => {
      if (type === 'group' && group) {
        this.selectedGroups.set(group.id, { ...group, member_count: group.student_count })
      }

      if (type === 'course' && course) this.selectedCourses.set(course.id, course)

      if (type === 'student' && student) this.selectedStudents.set(student.id, student)

      if (type === 'contact' && contact) {
        this.selectedContacts.set(contact.id, {
          ...contact,
          student: { id: contact.student_id, full_name: contact.student_full_name }
        })
      }
    })
  }

  @action
  updateScheduledBroadcast = async() => {
    if (!this.editingBroadcastId || !this.canSend || this.isSending) return

    this.setIsSending(true)
    this.setIsError(false)

    try {
      if (this.broadcastAttachments.length > 0) {
        this.setIsUploadingPage(true)
        this.setEditCodexPageUrl(await this.createCodexPage())
        this.setIsUploadingPage(false)
      } else {
        this.setEditCodexPageUrl(null)
      }

      await xhr.put(
        `commo/broadcast/${this.editingBroadcastId}/scheduled`,
        this.buildSendPayload(
          this.updateScheduledParams,
          this.updateScheduledAttachments
        )
      )

      const sentNow = this.isSendingScheduledNow

      if (sentNow) {
        await xhr.post(`commo/broadcast/${this.editingBroadcastId}/scheduled/send`)
      }

      this.updateScheduledBroadcastOk(sentNow)
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsSending(false)
      this.setIsUploadingPage(false)
    }
  }

  @action
  updateScheduledBroadcastOk = (sentNow = false) => {
    uiStore.addMessage(sentNow
      ? 'Broadcast was successfully sent!'
      : 'Broadcast was successfully updated!')

    if (sentNow) postEvent('sent-broadcast-message')

    this.setSelectedBroadcast(null)
    uiStore.setShowBroadcastInfo(false)
    this.resetBroadcast()
    this.refreshBroadcastList()
  }

  @action
  confirmSendScheduledNow = () => {
    this.setShowSendNowModal(false)
    this.updateScheduledBroadcast()
  }

  @action
  handleCloseBroadcastModal = () => {
    this.setShowDiscardModal(true)
  }

  @action
  discardBroadcast = () => {
    this.setShowDiscardModal(false)
    this.resetBroadcast()
  }

  @action
  handleSearchBroadcasts = (value) => {
    this.setSearchValueBroadcast(value.target.value)

    // Reset list on empty sender search box
    if(this.searchValueBroadcast.length == 0){
      this.clearData()
      this.fetchBroadcasts()
    }else {
      if (!this.shouldSearchBroadcast) return
      this.searchBroadcasts(this.searchValueBroadcast)
    }
  }

  @action
  handleSelectTerm = (term) => {
    this.courses.clear()
    this.selectedTerm = term
  }

  @action
  handleRoute = (route) => {
    this.routesVisited.push(route)
    this.setCurrentRoute(route)
  }

  @action
  handleOnNext = () => {
    if (this.currentRoute !== ROUTES.RECIPIENT_MANAGER) {
      if(this.currentRoute == ROUTES.MESSAGE_COMPOSER){
      this.fetchConversations()
      this.fetchDistrictTimeZone()
      this.setCurrentRoute(ROUTES.PREVIEW_AND_SEND)
      }else{
        this.setCurrentRoute(ROUTES.RECIPIENT_MANAGER)
      }
    } else {
      this.fetchConversations()
      this.setCurrentRoute(ROUTES.MESSAGE_COMPOSER)
    }

    this.routesVisited.push(this.currentRoute)
  }

  @action
  handleOnBack = () => {
    this.routesVisited.pop()
    this.setCurrentRoute(
      this.routesVisited[this.routesVisited.length - 1]
    )
  }

  // Codex Megas: create a hosted page from broadcastAttachments
  @action createCodexPage = async () => {
    const user = window.SSUser

    // Separate file attachments from link attachments
    const fileAttachments = this.broadcastAttachments.filter(a => a.type !== 'link')
    const linkAttachments = this.broadcastAttachments.filter(a => a.type === 'link')

    // The draft and the presigned PUT have to agree -- S3 signs the content type.
    const contentTypes = fileAttachments.map(a => uploadContentType(a.value))

    const files = fileAttachments.map((a, i) => ({
      filename: a.value.name,
      content_type: contentTypes[i]
    }))

    // 1. Draft
    const { data: draft } = await codexMegas.post('/pages/draft', {
      tenant_id: user.districtID,
      user_id: user.id,
      title: '',
      files
    })

    // 2. Upload each file to S3 via presigned URLs
    const perFileLoaded = new Array(fileAttachments.length).fill(0)
    const perFileTotal = fileAttachments.map(a => a.value.size || 0)
    const totalBytes = perFileTotal.reduce((sum, t) => sum + t, 0)

    this.setUploadProgress(0)

    await Promise.all(
      fileAttachments.map((a, i) =>
        axios.put(draft.upload_urls[a.value.name], a.value, {
          headers: { 'Content-Type': contentTypes[i] },
          onUploadProgress: (e) => {
            perFileLoaded[i] = e.loaded
            const loadedBytes = perFileLoaded.reduce((sum, l) => sum + l, 0)
            this.setUploadProgress(totalBytes > 0 ? Math.round((loadedBytes / totalBytes) * 100) : 100)
          }
        })
      )
    )

    // 3. Finalize — include full message body + link/file assets on the page
    const fileAssets = fileAttachments.map(a => ({
      filename: a.value.name,
      type: detectAssetType(a.value)
    }))

    const linkAssets = linkAttachments.map(a => ({
      filename: a.value,  // the URL string
      type: 'link'
    }))

    const assets = [...fileAssets, ...linkAssets]
    const pageBody = this.body

    // When auto-translate is on AND the body has translatable content,
    // send target languages so codex-megas generates per-language HTML
    // pages (index.{lang}.html). An empty/whitespace body has nothing to
    // translate, so we send an empty list to avoid turbine dead-reckoning
    // to URLs that codex-megas never wrote.
    const languages = (this.isAutoTranslate && this.hasTranslatableBody)
      ? this.LanguageInfo.languages
          .map(l => l.preferred_language)
          .filter(l => l && l !== 'na')
      : []

    const { data: result } = await codexMegas.post(
      `/pages/${draft.ulid}/finalize`,
      { title: '', body: pageBody, assets, languages }
    )

    return result.public_url
  }

  // Multipart only when there are files; nested objects flatten to params[only].
  buildSendPayload = (payload, attachments = []) => {
    if (_.isEmpty(attachments)) return payload

    const formData = new FormData()

    Object.keys(payload).forEach((key) => {
      const value = payload[key]

      if (_.isNil(value)) return

      if (_.isPlainObject(value)) {
        Object.keys(value).forEach((nested) => {
          formData.append(`${key}[${nested}]`, value[nested])
        })

        return
      }

      // Rack reads repeated `key[]` as an array; a bare append would join on commas.
      if (_.isArray(value)) {
        value.forEach((entry) => formData.append(`${key}[]`, entry))

        return
      }

      formData.append(key, value)
    })

    attachments.forEach((a) => {
      formData.append(BROADCAST_ATTACHMENTS_FIELD, a.value)
    })

    return formData
  }

  // One request, both channels. Attachments also get a codex page.
  @action sendBroadcast = async() => {
    try {
      this.setIsSending(true)
      this.setUploadProgress(0)

      let codexPageUrl = null
      if (this.broadcastAttachments.length > 0) {
        this.setIsUploadingPage(true)
        codexPageUrl = await this.createCodexPage()
        this.setIsUploadingPage(false)
      }

      const { data } = await xhr.post(
        '/commo/broadcast/send',
        this.buildSendPayload({
          ...this.sendParams,
          body:           this.body,
          subject:        BROADCAST_EMAIL_SUBJECT,
          // Coerced: turbine reads a missing send_sms as "send SMS".
          send_sms:       !!this.sendAsMessage,
          send_email:     !!this.sendAsEmail,
          codex_page_url: codexPageUrl,
          links:          this.broadcastLinks,
          // Absolute instant; null sends immediately.
          run_at:         this.runAt && this.runAt.toISOString()
        }, this.emailAttachments)
      )

      this.sendBroadcastOk(data)
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsSending(false)
      this.setIsUploadingPage(false)
    }
  }

  @action handleBroadcastSend = () => {
    if (this.isEditingScheduled) {
      // Firing ahead of run_at can't be undone, so confirm before it goes out.
      if (this.isSendingScheduledNow) return this.setShowSendNowModal(true)

      return this.updateScheduledBroadcast()
    }

    // Turbine accepts the oversized body and fails downstream, so catch it here.
    if (this.sendAsEmail && StringSize(this.body) > EMAIL_BODY_SIZE_LIMIT) {
      this.setIsError(STRING_SIZE_ERROR)

      return
    }

    return this.sendBroadcast()
  }

  @action sendBroadcastOk = (broadcast) => {
    const message = this.isScheduled
      ? 'Broadcast was successfully scheduled!'
      : 'Broadcast was successfully sent!'

    postEvent('sent-broadcast-message')
    uiStore.addMessage(message)

    // The scheduled tab lists scheduled rows only.
    if (!this.isScheduledTab || broadcast.status === SCHEDULED_STATUS) {
      this.createBroadcast(broadcast)
    }

    this.resetBroadcast()
  }

  @action onAutoTranslateChange = ({ target: { checked } }) => {
    this.setIsAutoTranslate(checked)
  }

  @action onOnlyStudentsChange = ({ target: { checked } }) => {
    this.setIsOnlyStudents(checked)
    this.fetchCount()
    this.fetchAvailableContacts(TYPES.PHONE)
    this.fetchAvailableContacts(TYPES.EMAIL)
  }

  @action addAttachment = (type, value) => {
    const newAttachment = {
      id:      `broadcast-attachment-${uuid()}`,
      type:    type,
      value:   value,
      altText: ''
    };

    this.setBroadcastAttachments([...this.broadcastAttachments, newAttachment]);
  }

  @action removeAttachment = (id) => {
    const updatedAttachments = this.broadcastAttachments.filter(item => item.id !== id);

    this.setBroadcastAttachments(updatedAttachments);
  }

  @action shouldRenderBroadcastAttachmentLinkInput = (value) => {
    this.updateBroadcastAttachmentLinkValue('');
    this.setRenderBroadcastLinkInput(value);
  }

  @action updateBroadcastAttachmentLinkValue = (value) => {
    this.setBroadcastAttachmentLinkValue(value);
  }

  @action addBroadcastAttachmentLink = () => {
    if(!URL.canParse(this.broadcastAttachmentLinkValue)){
      this.setIsError(
        {
          title: 'Invalid Link',
          message: 'The URL you entered is invalid. Please ensure it is a complete and correctly formatted web address (https://secure.schoolstatus.com) before trying again.'
        })

      return;
    }

    this.addAttachment('link', this.broadcastAttachmentLinkValue);
    this.updateBroadcastAttachmentLinkValue('');
    this.shouldRenderBroadcastAttachmentLinkInput(false);
  }

  // reason is a key of ATTACHMENT_ERROR_MESSAGES; only read for errors.
  @action displayAttachmentUploadNotification = (fileName, type, reason) => {
    const title = type == 'error'
      ? 'Upload Failed'
      : 'Upload Succeeded'

    const message = type == 'error'
      ? ATTACHMENT_ERROR_MESSAGES[reason](fileName)
      : `${fileName} uploaded successfully.`

    this.setIsError({title, message, type})
  }

  // Legacy cards: one method only, so selecting either clears the other.
  @action onSendAsMessageChange = ({ target: { checked } }) => {
    this.setSendAsMessage(checked)
    this.setSendAsEmail(!checked)
  }

  @action onSendAsEmailChange = ({ target: { checked } }) => {
    this.setSendAsEmail(checked)
    this.setSendAsMessage(!checked)
  }

  // Checkboxes: either, both, or neither. hasValidSendType gates the CTA.
  @action toggleSendAsMessage = ({ target: { checked } }) => {
    this.setSendAsMessage(checked)
  }

  @action toggleSendAsEmail = ({ target: { checked } }) => {
    this.setSendAsEmail(checked)
  }

  // Leaves the picker values alone so toggling doesn't discard them.
  @action onSendTimingChange = ({ target: { value } }) => {
    this.setSendTiming(value)
  }

  @action onScheduledDateChange = ({ target: { value } }) => {
    this.setScheduledDate(value)
  }

  @action onScheduledTimeChange = ({ target: { value } }) => {
    this.setScheduledTime(value)
  }


  // Could be something we add in the future
  // @action updateBroadcastAttachmentImageAltText = (id, value) => {
  //   const updatedAttachments = this.broadcastAttachments

  //   updatedAttachments.find(item => item.id == id).altText = value;

  //   debugger

  //   this.setBroadcastAttachments(updatedAttachments);
  // }

}

export default new BroadcastStore()
